import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { CheckCircle, Package, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { clearCart } = useCart();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/cart');
      return;
    }
    checkPaymentStatus();
  }, [sessionId]);

  const checkPaymentStatus = async (attempt = 0) => {
    const maxAttempts = 5;
    
    try {
      const response = await axios.get(
        `${API}/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setOrderDetails(response.data);
        clearCart();
        return;
      }

      if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        return;
      }

      // Continue polling if payment is still processing
      if (attempt < maxAttempts) {
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } else {
        setPaymentStatus('timeout');
      }
    } catch (error) {
      console.error('Failed to check payment status:', error);
      toast.error('Failed to verify payment');
      setPaymentStatus('error');
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="stars"></div>
      <Navbar />

      <div className="relative z-10 pt-32 pb-20 px-4">
        <div className="max-w-2xl mx-auto">
          {paymentStatus === 'checking' && (
            <div className="glass-card text-center py-12" data-testid="payment-checking">
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-purple-400 animate-spin" />
              <h2 className="text-3xl font-bold mb-4">Verifying Payment</h2>
              <p className="text-gray-400">Please wait while we confirm your payment...</p>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="glass-card text-center py-12" data-testid="payment-success">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h2 className="text-4xl font-bold mb-4">Payment Successful!</h2>
              <p className="text-gray-300 text-lg mb-8">Thank you for your order. Your payment has been processed successfully.</p>
              
              {orderDetails && (
                <div className="glass-card max-w-md mx-auto mb-8 text-left">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-400">Amount Paid</span>
                    <span className="font-bold">
                      {orderDetails.currency.toUpperCase()} {(orderDetails.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Status</span>
                    <span className="text-green-400 font-semibold capitalize">{orderDetails.payment_status}</span>
                  </div>
                </div>
              )}

              <div className="flex gap-4 justify-center">
                <button 
                  onClick={() => navigate('/account')}
                  className="btn-primary flex items-center gap-2"
                  data-testid="view-orders-btn"
                >
                  <Package className="w-5 h-5" />
                  View Orders
                </button>
                <button 
                  onClick={() => navigate('/shop')}
                  className="btn-secondary"
                  data-testid="continue-shopping-btn"
                >
                  Continue Shopping
                </button>
              </div>
            </div>
          )}

          {(paymentStatus === 'expired' || paymentStatus === 'timeout' || paymentStatus === 'error') && (
            <div className="glass-card text-center py-12" data-testid="payment-failed">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                <span className="text-4xl">✕</span>
              </div>
              <h2 className="text-4xl font-bold mb-4">Payment Issue</h2>
              <p className="text-gray-300 text-lg mb-8">
                {paymentStatus === 'expired' && 'Your payment session has expired.'}
                {paymentStatus === 'timeout' && 'Payment verification timed out. Please check your email for confirmation.'}
                {paymentStatus === 'error' && 'There was an error verifying your payment.'}
              </p>
              
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={() => navigate('/cart')}
                  className="btn-primary"
                  data-testid="retry-payment-btn"
                >
                  Try Again
                </button>
                <button 
                  onClick={() => navigate('/account')}
                  className="btn-secondary"
                  data-testid="check-orders-btn"
                >
                  Check Orders
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
